import * as React from "react";
import { useState, useMemo } from "react";
import {
  Text,
  Card,
  Button,
  RadioGroup,
  Radio,
  Label,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Signature24Regular } from "@fluentui/react-icons";
import { DiscussionDto } from "../../../services/discussions.service";
import { StatusItemDto } from "../../../services/status-items.service";
import {
  signAsManager,
  SpreadsheetReviewInfoResponse,
  SignRequest,
} from "../../../services/spreadsheets.service";
import { UserRole } from "../../../types/UserRole";
import { ExcelService } from "../../../services/excel.service";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { normalizeStatusItemStatus } from "../../../utils/statusItemHelpers";
import { useTypographyStyles } from "../../../styles/typography.styles";
import LiveReviewPoints from "./LiveReviewPoints";
import ReviewPointsSnapshot from "./ReviewPointsSnapshot";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { tryCreateCompletionCertificate } from "../completionCertificate";

const MANAGER_CHECKLIST_ITEMS = [
  "Current review points and pending matters cleared",
  "Final draft accounts discussed and agreed with client",
  "Analytic review of accounts completed",
  "All workpaper tabs have been signed off",
];

const useStyles = makeStyles({
  tabPanel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  checklistItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingBottom: tokens.spacingVerticalXS,
  },
  /** Used on the form-label wrapper so long question text wraps onto multiple lines. */
  formLabelWrap: {
    whiteSpace: "pre-line",
  },
  warningBanner: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    minHeight: "auto",
  },
  warningText: {
    color: tokens.colorPaletteYellowForeground2,
  },
  signButton: {
    alignSelf: "flex-start",
  },
});

interface ManagerTabProps {
  spreadsheetId: string | null;
  userRole: UserRole;
  reviewInfo: SpreadsheetReviewInfoResponse | null;
  discussions: DiscussionDto[];
  statusItems: StatusItemDto[];
  /**
   * Why the workbook can't be written to (read-only / never saved), or
   * null when it's writable. Sign-off writes the completion certificate
   * into the workbook, so a non-null value blocks signing entirely.
   */
  workbookBlockReason: string | null;
  onReviewInfoUpdated: (info: SpreadsheetReviewInfoResponse) => void;
}

/**
 * Manager sign-off tab: pre-signing checklist (or readonly recap), the live
 * list of open review points (or post-sign snapshot), the unreviewed status
 * item warning, and the Sign button. Edits are gated to the Reviewer role.
 */
const ManagerTab: React.FC<ManagerTabProps> = ({
  spreadsheetId,
  userRole,
  reviewInfo,
  discussions,
  statusItems,
  workbookBlockReason,
  onReviewInfoUpdated,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const track = useTrackEvent();

  const [managerResponses, setManagerResponses] = useState<string[]>(
    new Array(MANAGER_CHECKLIST_ITEMS.length).fill("")
  );
  const [isSigning, setIsSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allManagerAnswered = managerResponses.every((r) => r === "yes" || r === "no");
  const managerAlreadySigned = !!reviewInfo?.signingManagerEmail;
  // Partners can sign as manager too — partners are also senior reviewers and
  // sometimes act in both capacities on a job.
  const canEditManager = userRole === UserRole.Reviewer || userRole === UserRole.Partner;

  // Status items that are NOT at least "Quality Reviewed" — blocks manager sign-off
  const unreviewedStatusItems = useMemo(() => {
    const validStatuses = ["Quality Reviewed", "Partner Reviewed"];
    return statusItems
      .filter((item) => !item.isHidden)
      .filter((item) => !validStatuses.includes(normalizeStatusItemStatus(item.status)));
  }, [statusItems]);

  const allStatusItemsReviewed = unreviewedStatusItems.length === 0;

  const handleManagerResponseChange = (index: number, value: string) => {
    setManagerResponses((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleManagerSign = async () => {
    if (!spreadsheetId || !allManagerAnswered || !allStatusItemsReviewed || managerAlreadySigned) return;
    if (workbookBlockReason) return;

    setIsSigning(true);
    setError(null);
    try {
      // Re-check immediately before committing. The mount-time probe
      // drives the disabled state, but the workbook can flip to
      // read-only while the page is open (someone else opens the file).
      // Sign-off is irreversible server-side, so verify first — better
      // to refuse than to commit a sign whose certificate can't land.
      await ExcelService.assertWorkbookWritable("the sign-off");

      const request: SignRequest = {
        checklistResponses: MANAGER_CHECKLIST_ITEMS.map((text, index) => ({
          index,
          text,
          response: managerResponses[index] as "yes" | "no",
        })),
      };
      const updatedInfo = await signAsManager(spreadsheetId, request);
      track("signoff.manager_signed");
      onReviewInfoUpdated(updatedInfo);
      // Regenerate the completion certificate with the manager's details
      // so a subsequent partner can see at a glance that review has
      // happened. The certificate is fully rewritten when the partner
      // signs later, which extends it with the Partner Sign-off block.
      //
      // Never throws — returns a message when the certificate couldn't be
      // written (read-only workbook, protected sheet, save failure). The
      // sign-off itself has already succeeded at this point, so we surface
      // the message without unwinding anything.
      const certWarning = await tryCreateCompletionCertificate(updatedInfo, spreadsheetId, statusItems);
      if (certWarning) {
        setError(certWarning);
      } else {
        track("completion_certificate.generated");
      }
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err));
    } finally {
      setIsSigning(false);
    }
  };

  return (
    <div className={styles.tabPanel}>
      <div className={styles.section}>
        <Text className={typography.subsectionTitle}>Pre-signing Checklist</Text>
        {managerAlreadySigned && reviewInfo?.managerChecklistResponses ? (
          // Show stored responses readonly after signing
          reviewInfo.managerChecklistResponses.items.map((item, index) => (
            <div key={index} className={styles.checklistItem}>
              <Label className={`${typography.formLabel} ${styles.formLabelWrap}`}>{item.text}</Label>
              <Text
                className={item.response === "yes" ? typography.responseYes : typography.responseNo}
              >
                {item.response === "yes" ? "Yes" : "No"}
              </Text>
            </div>
          ))
        ) : (
          // Interactive radio buttons before signing
          MANAGER_CHECKLIST_ITEMS.map((item, index) => (
            <div key={index} className={styles.checklistItem}>
              <Label className={`${typography.formLabel} ${styles.formLabelWrap}`}>{item}</Label>
              <RadioGroup
                layout="horizontal"
                value={managerResponses[index]}
                onChange={(_, data) => handleManagerResponseChange(index, data.value)}
                disabled={!canEditManager}
              >
                <Radio value="yes" label="Yes" />
                <Radio value="no" label="No" />
              </RadioGroup>
            </div>
          ))
        )}
      </div>

      {managerAlreadySigned && reviewInfo?.managerReviewPointsSnapshot ? (
        <ReviewPointsSnapshot
          snapshot={reviewInfo.managerReviewPointsSnapshot}
          title="Review Points at Sign-off"
        />
      ) : (
        <LiveReviewPoints discussions={discussions} />
      )}

      {!managerAlreadySigned && !allStatusItemsReviewed && (
        <Card className={styles.warningBanner}>
          <Text size={200} className={styles.warningText}>
            All status items must be Quality Reviewed before signing.{" "}
            {unreviewedStatusItems.length} item{unreviewedStatusItems.length !== 1 ? "s" : ""} not yet reviewed:{" "}
            {unreviewedStatusItems.map((item) => item.name).join(", ")}
          </Text>
        </Card>
      )}

      {!managerAlreadySigned && workbookBlockReason && (
        <Card className={styles.warningBanner}>
          <Text size={200} className={styles.warningText}>
            {workbookBlockReason}
          </Text>
        </Card>
      )}

      {error && (
        <Text className={typography.errorText}>{error}</Text>
      )}

      {!managerAlreadySigned && (
        <Button
          className={styles.signButton}
          appearance="primary"
          icon={<Signature24Regular />}
          disabled={
            !canEditManager ||
            !allManagerAnswered ||
            !allStatusItemsReviewed ||
            isSigning ||
            !!workbookBlockReason
          }
          onClick={handleManagerSign}
          size="medium"
        >
          {isSigning ? "Signing..." : "Sign"}
        </Button>
      )}
    </div>
  );
};

export default ManagerTab;
